// Imports for external libraries go here.
import React, { FC, useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';

import { InputTextField, Button } from '@marriott/mi-ui-library';

import { TypeAndSearchWithLinkProps } from './TypeAndSearchWithLink.types';
import { StyledTypeAndSearchWithLink } from './TypeAndSearchWithLink.styles';

export const TypeAndSearchWithLink: FC<TypeAndSearchWithLinkProps> = ({
  searchFieldLabel,
  searchResultFirstFieldValue,
  searchResultLastFieldValue,
  isLinkRequired = false,
  linkLabel,
  linkUrl,
  isLinkDataLoading = false,
  inputType,
  testId,
  inputMaxLength,
  infoLabel,
  name,
  hotelSearchResults,
  getSearchValue,
  showErrorMsg,
  inputValue,
  className,
}) => {
  const [searchResult, setSearchResult] = useState<string | null>(inputValue || null);
  const [showResultList, setShowResultList] = useState(true);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  const isSearchResultIsFromFoundResultList = useCallback(() => {
    return hotelSearchResults?.some(item => item.toLowerCase() === searchResult?.toLowerCase());
  }, [searchResult, hotelSearchResults]);

  useEffect(() => {
    const hideSearchResultsListAndSetSearchResultToNull = () => {
      setShowResultList(false);
      if (!isSearchResultIsFromFoundResultList()) {
        setSearchResult(null);
        getSearchValue?.('', true, -1);
      }
    };

    const handleWindowClick = () => {
      hideSearchResultsListAndSetSearchResultToNull();
    };

    const setSuggestionResult = (
      selectedIndex: number,
      hotelSearchResults: string[],
      hideResultList: boolean = true
    ) => {
      const selectedItem = hotelSearchResults[selectedIndex];
      setSearchResult(selectedItem);
      hideResultList && setShowResultList(false);
      getSearchValue?.(selectedItem, false, selectedIndex);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const currentActiveElement = document.activeElement as HTMLInputElement;
      if (event.key === 'Escape') {
        hideSearchResultsListAndSetSearchResultToNull();
      } else if (event.key === 'ArrowDown' && hotelSearchResults && currentActiveElement?.name === name) {
        setShowResultList(true);

        const newIndex = highlightedIndex === hotelSearchResults.length - 1 ? 0 : highlightedIndex + 1;
        setHighlightedIndex(newIndex);

        setSuggestionResult(newIndex, hotelSearchResults, false);
      } else if (event.key === 'ArrowUp' && hotelSearchResults && currentActiveElement?.name === name) {
        setShowResultList(true);

        const newIndex =
          highlightedIndex === 0 || highlightedIndex === -1 ? hotelSearchResults.length - 1 : highlightedIndex - 1;
        setHighlightedIndex(newIndex);

        setSuggestionResult(newIndex, hotelSearchResults, false);
      } else if (event.key === 'Enter' && hotelSearchResults) {
        const index = highlightedIndex !== -1 ? highlightedIndex : 0;
        setSuggestionResult(index, hotelSearchResults, true);
      } else if (event.key === 'Tab') {
        if (!hotelSearchResults) {
          hideSearchResultsListAndSetSearchResultToNull();
          return;
        }
        const index = highlightedIndex !== -1 ? highlightedIndex : 0;
        setSuggestionResult(index, hotelSearchResults, true);
      }
    };

    window.addEventListener('click', handleWindowClick);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('click', handleWindowClick);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSearchResultIsFromFoundResultList, getSearchValue, hotelSearchResults, highlightedIndex, name]);

  useEffect(() => {
    if (hotelSearchResults && hotelSearchResults.length > 0) {
      setShowResultList(true);
      return;
    }
    setShowResultList(false);
  }, [hotelSearchResults]);

  useEffect(() => {
    setSearchResult(inputValue || null);
  }, [inputValue]);

  const onItemSelected = (event: React.MouseEvent<HTMLLIElement>, index: number) => {
    const target = event.currentTarget as HTMLLIElement;
    setSearchResult(target.textContent);
    setShowResultList(false);
    getSearchValue?.(target.textContent!, false, index);
    setHighlightedIndex(index);
  };

  const clearSearchResultAndHideSearchResultList = () => {
    setSearchResult(null);
    setShowResultList(false);
    getSearchValue?.('', true, -1);
  };

  const getHighLightedResult = (result: string, query: string | null) => {
    if (!query) return result;

    let startIndex = 0;
    const parts = [];

    while (startIndex < result.length) {
      const index = result.toLowerCase().indexOf(query.toLowerCase(), startIndex);
      if (index === -1) {
        parts.push(result.slice(startIndex));
        break;
      }

      parts.push(result.slice(startIndex, index));
      parts.push(<b key={index}>{result.slice(index, index + query.length)}</b>);
      startIndex = index + query.length;
    }

    return parts;
  };

  return (
    <StyledTypeAndSearchWithLink
      className={className}
      data-component-name="m-legal-TypeAndSearchWithLink"
      data-testid="legal-TypeAndSearchWithLink"
    >
      <div className="search-container">
        <InputTextField
          className="m-input-field"
          label={searchFieldLabel.label}
          type={inputType}
          testId={testId}
          inputValue={searchResult || ''}
          showErrorMessage={showErrorMsg}
          messageToShow={searchFieldLabel.errorLabel}
          inputMaxLength={inputMaxLength}
          infoLabel={infoLabel}
          getInputValue={(val: string) => {
            setSearchResult(val);
            getSearchValue?.(val, true, -1);
            setHighlightedIndex(-1);
          }}
          getInputProps={() => ({
            autoComplete: 'off',
            name,
          })}
        />
        <span
          className={clsx('icon-cancel clear-btn', showErrorMsg && 'isError')}
          aria-label="clear search result"
          onClick={clearSearchResultAndHideSearchResultList}
        ></span>
        {showResultList && hotelSearchResults && searchResult && (
          <div className="search_scroll" aria-label="search result">
            <ul role="listbox" aria-labelledby="search result">
              <li onClick={e => e.stopPropagation()}>{searchResultFirstFieldValue}</li>
              {hotelSearchResults.map((hotelName, index) => (
                <li
                  key={index}
                  role="menuitem"
                  onClick={event => onItemSelected(event, index)}
                  className={clsx({ 'highlighted-item': highlightedIndex === index })}
                >
                  {getHighLightedResult(hotelName, searchResult)}
                </li>
              ))}
              <li onClick={e => e.stopPropagation()}>{searchResultLastFieldValue}</li>
            </ul>
          </div>
        )}
      </div>
      {isLinkRequired && (
        <Button
          className={clsx('m-button-primary m-button-s mt-1 link-btn', { 'skeleton-loader': isLinkDataLoading })}
          isLink={true}
          target="_blank"
          href={linkUrl}
        >
          {linkLabel}
        </Button>
      )}
    </StyledTypeAndSearchWithLink>
  );
};
